import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  HelpCircle,
  Search,
  User,
  Dog,
  Trees,
  Briefcase,
  Building2,
  Ticket,
  Shield,
  Dna,
  Heart,
  MessageSquare,
  CreditCard,
  Globe,
  Mail,
  Users
} from 'lucide-react';

// Map URL topic params to FAQ category IDs
const topicToCategoryMap = {
  'dna': 'health',        // DNA testing → health category
  'health': 'health',
  'research': 'community', // Research collaboration → community
  'pets': 'pets',
  'ancestry': 'pets',
  'matchmaking': 'pets',
  'adoption': 'organizations',
  'clubs': 'community',
  'membership': 'membership',
  'privacy': 'privacy'
};

const FAQ = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [openCategories, setOpenCategories] = useState([]);
  
  // Handle topic query parameter - auto-expand relevant category
  useEffect(() => {
    const topic = searchParams.get('topic');
    if (topic && topicToCategoryMap[topic]) {
      setOpenCategories([topicToCategoryMap[topic]]);
      // Scroll to the category after a brief delay
      setTimeout(() => {
        const element = document.getElementById(`faq-${topicToCategoryMap[topic]}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [searchParams]);

  const faqCategories = [
    {
      id: 'general',
      title: 'General',
      icon: HelpCircle,
      color: 'bg-blue-100 text-blue-600',
      questions: [
        {
          q: "What is All American Dog Club?",
          a: "All American Dog Club (AADC) is a nonprofit community platform for dog owners. More than just a social network, we're a venue for club operations and real-world collaboration among dog enthusiasts. We connect dog lovers, provide resources for pet care, help you find dog parks and services, and coordinate club activities. As a 501(c)(3) nonprofit organization, we're committed to serving the dog community without commercial exploitation."
        },
        {
          q: "Is AADC free to use?",
          a: "Yes! AADC is free to join and use. We offer all core features at no cost, including pet profiles, forums, dog park finder, service directory, and community features. We're supported by nonprofit donations and partner sponsorships, not by selling your data."
        },
        {
          q: "I found something that doesn't look right. What should I do?",
          a: "We're still in beta, and there may be some items that were inserted during testing that are not real or accurate. If you find something that seems untrue, incomplete, or just doesn't look right, please use the Feedback button (bottom right corner) to let us know so we can review and remove them. Your help makes AADC better for everyone!"
        },
        {
          q: "What types of pets does AADC support?",
          a: "While our focus is on dogs, AADC now supports 8 pet types: Dogs, Cats, Birds, Fish, Reptiles, Small Pets (hamsters, rabbits, guinea pigs), Horses, and Other pets. Each pet type has its own themed experience and relevant resources."
        },
        {
          q: "Is AADC available internationally?",
          a: "Yes! AADC is available worldwide. Our platform supports multiple languages including English, Spanish, German, French, Portuguese, Japanese, and Chinese. However, some features like the dog park database are currently more comprehensive in the United States."
        }
      ]
    },
    {
      id: 'account',
      title: 'Account & Membership',
      icon: User,
      color: 'bg-green-100 text-green-600',
      questions: [
        {
          q: "How do I create an account?",
          a: "Click 'Join Now' on our homepage and fill in your email, username, display name, and password. Once registered, you can immediately start adding your pets, joining forums, and connecting with other pet owners."
        },
        {
          q: "What are the different member classes?",
          a: "AADC offers several member classes to serve different needs in our community:\n\n• FREE - Our default membership when you register. You can create up to 3 posts per day, 10 comments, add 1 pet, send 5 messages, and join 2 events per month. Free members see all ads including Google and sponsor ads.\n\n• FULL MEMBER - Upgraded members who support the platform. Full Members have no daily limits on posts, comments, or messages, can add up to 5 pets, can vote in community elections, use Pet Friends and Matchmaking features, and sell in the Marketplace. Full Members only see sponsor and special offer ads (no Google ads).\n\n• PREMIUM - Our highest tier members enjoy all Full Member benefits plus unlimited pets, an ad-free experience, and priority support.\n\n• VOLUNTEER - Community heroes who help answer questions and moderate discussions. Volunteers are 'first responders' for their pet type expertise. They enjoy Full Member-level benefits as a thank you for their service.\n\n• BREEDER - Registered dog breeders who have verified their breeding program. Breeders have access to special tools for managing litters, health certifications, and connecting with potential puppy families.\n\n• PROFESSIONAL - Verified pet professionals including veterinarians, trainers, groomers, and behaviorists. Professionals can create business profiles, respond to community questions with their credentials displayed, and access the Professional Forums.\n\n• VENDOR - Business partners who sell products or services through our Marketplace. Vendors have access to business analytics and promotional tools."
        },
        {
          q: "What is a Beta User?",
          a: "Beta Users are early adopters who help shape AADC by testing new features, reporting bugs, and suggesting improvements. Beta users get:\n\n• Access to the Beta Feedback Forum to submit suggestions and bug reports\n• Access to the Club Name Suggestions forum\n• First access to new features before public release\n• Automatic Charter Member status with special pricing\n• Ability to sponsor new clubs for other pet types\n• Discount DNA tests and monitoring\n• Search for compatible matches and ancestry\n• A special BETA badge on their profile\n\nTo apply for beta access, contact us or look for beta program announcements in the community forums."
        },
        {
          q: "What is Charter Membership?",
          a: "Charter Members are founding supporters who joined during our early growth phase. Charter members receive:\n\n• 75% discount on membership for the duration of their subscription\n• Pricing options: 6 months ($14.98), 1 year ($29.97), 2 years ($59.94), or 3 years ($89.91)\n• No monthly option - commitment helps us plan for the community's future\n• Ability to sponsor and lead clubs for new pet types\n• Exclusive Charter Member Lounge forum\n• Charter Member badge that stays on your profile forever\n\nAll Beta Users automatically receive Charter Member status and pricing."
        },
        {
          q: "What features are limited for FREE members?",
          a: "FREE members have some daily/monthly limits to ensure fair resource usage:\n\n• Posts: 3 per day\n• Comments: 10 per day\n• Pets: 1 pet profile\n• Messages: 5 per day\n• Events: 2 RSVPs per month\n\nFREE members cannot:\n• Vote in community elections\n• Use Pet Friends feature\n• Use Matchmaking feature\n• Sell items in the Marketplace\n\nUpgrade to PAID membership to remove these limits!"
        },
        {
          q: "How do I upgrade my membership?",
          a: "Go to Settings > Membership to view upgrade options. We offer 6-month, 1-year, 2-year, and 3-year plans. Charter members and Beta users get 75% off! Payment is processed securely through Stripe."
        },
        {
          q: "How do I reset my password?",
          a: "Click 'Sign In' and then 'Forgot Password'. Enter your email address, and we'll send you a link to reset your password. The link expires after 24 hours for security."
        },
        {
          q: "Can I delete my account?",
          a: "Yes. Go to Settings > Account > Delete Account. This will permanently remove your profile, pets, and all associated data. This action cannot be undone, so please download any data you want to keep first."
        },
        {
          q: "What's the difference between a FREE account and Club Membership?",
          a: "This is an important distinction!\n\n• FREE Account - When you register, you get a FREE platform account. This gives you access to the AADC platform (with some daily limits), but you are NOT a full club member.\n\n• Club Membership - When you upgrade to PAID or PREMIUM, you become a full member of the All American Dog Club. Club members get full platform access PLUS exclusive club resources, voting rights in club elections, access to the member directory, and more.\n\nThink of it this way: FREE = Platform access only. PAID/PREMIUM = Platform access + Full club membership."
        },
        {
          q: "What is the All-American Dog Club?",
          a: "The All-American Dog Club is our flagship kennel club, celebrating the 'All-American Dog' - a term the AKC uses for mixed breed dogs in competitions. Our club embraces ALL dogs, purebred and mixed alike.\n\n• Tagline: 'Every Dog Is a Champion'\n• Mission: Reduce ownership costs, promote events, support adoption organizations\n• Club Resources: AKC bylaws, breed club examples, educational materials\n• Future: International expansion planned - the name will evolve!\n\nAs we grow, more clubs will be available for different pet types and regions."
        },
        {
          q: "What are Club Resources?",
          a: "Club Resources are exclusive materials available only to club members (PAID/PREMIUM). They include:\n\n• AKC bylaws and governance documents\n• Sample bylaws from major breed clubs (GSDCA, GRCA, DPCA, etc.)\n• Educational guides and training materials\n• Forms and templates\n• Links to Petfinder, Adopt-a-Pet, and adoption resources\n\nFREE account holders can see the Club Resources page but must upgrade to access the materials."
        }
      ]
    },
    {
      id: 'pets',
      title: 'Pet Profiles',
      icon: Dog,
      color: 'bg-amber-100 text-amber-600',
      questions: [
        {
          q: "What types of pets does AADC support?",
          a: "AADC currently supports 8 pet types: Dogs, Cats, Birds, Fish, Reptiles, Small Pets (hamsters, rabbits, guinea pigs), Horses, and Other pets. Each pet type has its own themed experience with relevant resources, health tracking, and community features. We're continuously expanding support for more species!"
        },
        {
          q: "How do I add a pet to my profile?",
          a: "Go to 'My Pets' from the navigation menu and click 'Add Pet'. Fill in your pet's name, type, breed (if applicable), age, and upload a photo. You can add as many pets as your membership tier allows!"
        },
        {
          q: "Can my pets have friends on AADC?",
          a: "Yes! Pets can have their own friend connections. When you meet other pet owners at parks or events, your pets can become 'friends' on the platform, creating a social network for your furry companions."
        },
        {
          q: "How do I track my pet's health information?",
          a: "Use our Genetic Health Checklist feature. Go to Health > Your Pet's Health Checklist. You can record DNA test results from providers like Embark or Wisdom Panel, upload health documents, and track vaccinations and vet visits."
        },
        {
          q: "Can I share my pet's profile with others?",
          a: "Yes! Each pet profile can be set to Public, Members Only, Friends Only, or Private. Public profiles can be shared via link with anyone, while private profiles are only visible to you."
        }
      ]
    },
    {
      id: 'parks',
      title: 'Dog Parks',
      icon: Trees,
      color: 'bg-green-100 text-green-600',
      questions: [
        {
          q: "How do I find dog parks near me?",
          a: "Go to 'Parks' in the navigation. You can search by city, state, or zip code. We have 34+ real dog parks across major US cities including NYC, LA, Chicago, Houston, Dallas, Seattle, Denver, and more."
        },
        {
          q: "What information is available for each park?",
          a: "Each park listing includes: name, address with zip code, hours of operation, size in acres, rating, amenities (fenced, water fountains, separate areas for small/large dogs, agility equipment, etc.), and user reviews."
        },
        {
          q: "How does the check-in feature work?",
          a: "When you visit a dog park, you can 'check in' to let other members know you're there. This helps others see who's at the park and find playmates for their dogs. Check-ins automatically expire when you leave."
        },
        {
          q: "Can I add a new dog park to the database?",
          a: "Yes! Registered members can submit new dog parks. Click 'Add a Dog Park' on the Parks page, fill in the details, and our community will verify the information."
        }
      ]
    },
    {
      id: 'services',
      title: 'Services Directory',
      icon: Briefcase,
      color: 'bg-purple-100 text-purple-600',
      questions: [
        {
          q: "What types of services are listed?",
          a: "Our directory includes veterinarians, groomers, trainers, pet sitters, dog walkers, boarding facilities, pet stores, emergency vets, behaviorists, pet insurance providers, and more."
        },
        {
          q: "How are service providers rated?",
          a: "Members can leave reviews with ratings (1-5 stars), written feedback, and pros/cons. We also display verification badges for providers with confirmed licenses or insurance."
        },
        {
          q: "Can I save my favorite service providers?",
          a: "Yes! Use 'My Care Team' to save your personal veterinarian, groomer, trainer, and other providers for quick access. You can even share your Care Team with family members for emergencies."
        },
        {
          q: "How do I become a listed service provider?",
          a: "Service providers can create a business profile by registering as a 'Professional' or 'Vendor' member. You can then claim or create your business listing and respond to reviews."
        }
      ]
    },
    {
      id: 'organizations',
      title: 'Organizations & Clubs',
      icon: Building2,
      color: 'bg-indigo-100 text-indigo-600',
      questions: [
        {
          q: "What types of organizations are on AADC?",
          a: "We support breed clubs, rescue organizations, training clubs, kennel clubs, pet welfare organizations, veterinary associations, grooming associations, agility clubs, service dog organizations, therapy dog organizations, and breed registries."
        },
        {
          q: "How do I join an organization?",
          a: "Browse Organizations & Clubs from the navigation, find one you're interested in, and click 'Join'. Some organizations may have membership requirements or fees that are handled outside our platform."
        },
        {
          q: "Can I create a new organization?",
          a: "Yes! Click 'Add Organization' and fill in details like name, type, description, mission statement, website, and location. You'll automatically become the admin of organizations you create."
        },
        {
          q: "What are Partner organizations?",
          a: "Partner organizations have been verified by AADC and may offer special benefits to our members. Look for the 'Partner' badge on organization profiles."
        }
      ]
    },
    {
      id: 'deals',
      title: 'Partner Deals',
      icon: Ticket,
      color: 'bg-pink-100 text-pink-600',
      questions: [
        {
          q: "What are Partner Deals?",
          a: "Partner Deals are exclusive discounts and offers from pet businesses that partner with AADC. These include percentage discounts, free items, BOGO offers, member pricing, and more."
        },
        {
          q: "Who can access Partner Deals?",
          a: "All registered members can access Partner Deals - you don't need a paid membership! Simply create a free account to view and redeem available offers."
        },
        {
          q: "How do I redeem a deal?",
          a: "Click 'View & Redeem' on any deal. You'll see the promo code (if applicable) and redemption instructions. Some deals are online-only, some are in-store, and some work both ways."
        },
        {
          q: "Do deals expire?",
          a: "Yes, most deals have expiration dates and may have limits on how many times you can redeem them. Check the deal details for specific terms and conditions."
        }
      ]
    },
    {
      id: 'health',
      title: 'Genetic Health',
      icon: Dna,
      color: 'bg-red-100 text-red-600',
      questions: [
        {
          q: "What is the Genetic Health Checklist?",
          a: "The Genetic Health Checklist helps you track your pet's genetic tests and health conditions. It shows breed-specific recommended tests, your pet's results, and identifies any conditions requiring attention."
        },
        {
          q: "Which DNA testing providers are supported?",
          a: "We support results from Embark, Wisdom Panel, MyDogDNA, Orivet, DNA My Dog, PennGen, OFA, and other veterinary genetic testing services."
        },
        {
          q: "How do I import my pet's DNA results?",
          a: "AADC is working on direct integrations with major DNA testing labs. Soon you'll be able to import genetic results directly into your pet's profile with one click. Currently, you can manually enter results from your DNA test provider. This data powers ancestry insights, health markers, breed composition analysis, and compatibility matching."
        },
        {
          q: "Can I compare my dog's genetics with other dogs?",
          a: "Yes! The Compare feature lets you compare your dog's genetic profile with breed averages or specific dogs whose owners have allowed comparisons. This is useful for breeders and health research."
        },
        {
          q: "How is my pet's health data protected?",
          a: "You have full control over your pet's health data privacy. Each test and document can be set to Public, Members Only, Friends Only, or Private. You can also choose whether to share anonymous data for breed research."
        }
      ]
    },
    {
      id: 'privacy',
      title: 'Privacy & Data',
      icon: Shield,
      color: 'bg-slate-100 text-slate-600',
      questions: [
        {
          q: "Does AADC sell my data?",
          a: "Absolutely not. As a 501(c)(3) nonprofit organization, AADC will never sell your personal data or be sold to a commercial company that could exploit our community for profit. This is our core promise."
        },
        {
          q: "What data does AADC collect?",
          a: "We collect information you provide (profile, pets, posts) and basic usage data to improve the platform. We use anonymized, aggregated data for breed health research only with your consent. See our Privacy Policy for full details."
        },
        {
          q: "Can I control who sees my information?",
          a: "Yes! You have granular privacy controls for your profile, pets, health records, and documents. Set each to Public, Members Only, Friends Only, or Private. Admins can also configure which categories are visible to guests."
        },
        {
          q: "How do I download my data?",
          a: "Go to Settings > Privacy > Download My Data. We'll compile your profile, pets, posts, and other information into a downloadable file. This complies with data portability regulations."
        }
      ]
    },
    {
      id: 'community',
      title: 'Community & Forums',
      icon: MessageSquare,
      color: 'bg-teal-100 text-teal-600',
      questions: [
        {
          q: "Can I browse forums without an account?",
          a: "Yes! Forums are publicly accessible for reading. However, you'll need to create a free account to post, comment, like, or interact with other members."
        },
        {
          q: "What are the forum categories?",
          a: "We have categories for Care Tips, Breed Discussion, Health Questions, Showcase (sharing photos), News, and General Discussion. Posts can be tagged for easier discovery."
        },
        {
          q: "How do I report inappropriate content?",
          a: "Click the three-dot menu on any post or comment and select 'Report'. Our volunteer moderators review reports and take action according to our community guidelines."
        },
        {
          q: "Can I connect with other pet owners?",
          a: "Yes! You can send friend requests to other members, follow their profiles, and message them directly. Building connections helps you find playmates for your pets and share experiences."
        },
        {
          q: "What are the forum rules?",
          a: "AADC is a family-friendly community. All discussions must be respectful, constructive, and appropriate for all ages. No harassment, spam, or inappropriate content. See our Community Guidelines for full details."
        },
        {
          q: "How can I participate in veterinary research?",
          a: "AADC partners with veterinary schools and research institutions to advance animal health. You can opt-in to share anonymized pet health data for breed studies, genetic research, and treatment development. Your participation is always voluntary and your privacy is protected. Look for research opportunities in your pet's health section or community announcements."
        },
        {
          q: "What research programs does AADC support?",
          a: "We collaborate on studies including:\n\n• Breed-specific health condition tracking\n• Genetic marker research for hereditary diseases\n• Nutrition and longevity studies\n• Behavioral research projects\n• Cancer and chronic disease research\n\nYour pet's data could help save lives and improve treatments for future generations. All participation is opt-in and data is anonymized."
        }
      ]
    },
    {
      id: 'membership',
      title: 'Membership Tiers',
      icon: CreditCard,
      color: 'bg-emerald-100 text-emerald-600',
      questions: [
        {
          q: "What's the difference between FREE and PAID membership?",
          a: "FREE membership is our default tier with some usage limits (3 posts/day, 1 pet, 5 messages/day). You see all ads including Google ads.\n\nPAID membership removes all limits, lets you add up to 5 pets, vote in elections, use Pet Friends & Matchmaking, sell in Marketplace, and you only see sponsor ads (no Google ads). It's our way of saying thank you for supporting the community!"
        },
        {
          q: "What do PREMIUM members get?",
          a: "PREMIUM is our highest tier and includes everything in PAID plus:\n\n• Unlimited pets\n• Completely ad-free experience\n• Priority support\n• Early access to new features\n\nPREMIUM members help ensure AADC stays sustainable while enjoying the cleanest experience possible."
        },
        {
          q: "How do I become a VOLUNTEER?",
          a: "Volunteers are community members who help answer questions and moderate discussions. We look for:\n\n• Active, helpful community members\n• Expertise in specific pet types or topics\n• Good standing in the community\n• Willingness to be a 'first responder' for questions\n\nVolunteers enjoy PAID-level benefits as a thank you. Express interest in the forums or apply when we post volunteer calls!"
        },
        {
          q: "How do I register as a BREEDER?",
          a: "Breeders can apply for verified status by providing:\n\n• Breeding program information\n• Health testing documentation\n• Kennel club registrations (if applicable)\n• References from veterinarians or breed clubs\n\nVerified breeders get special tools for managing litters, displaying health certifications, and connecting with families. Apply through Settings > Become a Breeder."
        },
        {
          q: "How do I join as a PROFESSIONAL?",
          a: "Pet professionals (vets, trainers, groomers, behaviorists) can apply for Professional status to:\n\n• Display credentials on your profile\n• Access Professional Forums\n• Create a business profile\n• Respond to questions with your professional badge\n\nApply through Settings > Professional Verification with your license/certification info."
        },
        {
          q: "What does VENDOR status include?",
          a: "Vendors are businesses that sell products or services through our Marketplace. Vendor benefits include:\n\n• Business profile with logo and description\n• Marketplace storefront\n• Sales analytics dashboard\n• Promotional tools and featured placement options\n\nApply through Settings > Become a Vendor with your business information."
        },
        {
          q: "What is RESEARCHER membership?",
          a: "RESEARCHER is a paid membership tier for verified researchers and institutions conducting veterinary or animal behavior studies. Researchers get:\n\n• Post and manage research studies on our platform\n• Recruit participants from our community\n• Access anonymized aggregate data (with user consent)\n• Analytics dashboard for study engagement\n• Participant messaging tools\n\nVerification requires institutional credentials. This helps connect our community with legitimate research that advances animal health and welfare."
        },
        {
          q: "What ads do different members see?",
          a: "Ad visibility depends on your membership:\n\n• FREE members: See all ads (Google + sponsor/partner ads)\n• PAID members: Only see sponsor and special offer ads (no Google ads)\n• PREMIUM members: No ads at all\n• VOLUNTEERS: See sponsor ads only (no Google ads)\n\nOur sponsors are carefully selected pet-related businesses that offer value to our community."
        }
      ]
    }
  ];

  // Filter questions based on search
  const filteredCategories = searchQuery
    ? faqCategories.map(category => ({
        ...category,
        questions: category.questions.filter(
          q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
               q.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(category => category.questions.length > 0)
    : faqCategories;

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="FAQ"
        description="Frequently asked questions about All American Dog Club membership, features, and community guidelines."
        url="/faq"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary to-secondary text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-8 h-8" />
          </div>
          <h1 className="text-4xl font-heading font-medium mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
            Find answers to common questions about All American Dog Club, your pets, and our community features.
          </p>
          
          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-white text-foreground"
            />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
          {faqCategories.slice(0, 5).map((category) => {
            const Icon = category.icon;
            return (
              <a
                key={category.id}
                href={`#faq-${category.id}`}
                className="block"
              >
                <Card className="card-interactive text-center p-4 h-full">
                  <div className={`w-12 h-12 rounded-full ${category.color} flex items-center justify-center mx-auto mb-2`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <p className="font-medium text-sm">{category.title}</p>
                </Card>
              </a>
            );
          })}
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-4xl mx-auto space-y-8">
          {filteredCategories.length === 0 ? (
            <Card className="text-center py-16">
              <CardContent>
                <Search className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="font-heading text-xl mb-2">No results found</h3>
                <p className="text-muted-foreground">
                  Try different keywords or browse all categories below.
                </p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setSearchQuery('')}
                >
                  Clear Search
                </Button>
              </CardContent>
            </Card>
          ) : (
            filteredCategories.map((category) => {
              const Icon = category.icon;
              return (
                <div key={category.id} id={`faq-${category.id}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-10 h-10 rounded-full ${category.color} flex items-center justify-center`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <h2 className="text-2xl font-heading font-medium">{category.title}</h2>
                  </div>
                  
                  <Card>
                    <Accordion 
                      type="single" 
                      collapsible 
                      className="w-full"
                      defaultValue={openCategories.includes(category.id) ? `${category.id}-0` : undefined}
                    >
                      {category.questions.map((item, index) => (
                        <AccordionItem key={`${category.id}-${index}`} value={`${category.id}-${index}`}>
                          <AccordionTrigger className="px-4 hover:no-underline">
                            <span className="text-left font-medium">{item.q}</span>
                          </AccordionTrigger>
                          <AccordionContent className="px-4 pb-4">
                            <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                              {item.a}
                            </p>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </Card>
                </div>
              );
            })
          )}
        </div>

        {/* Still Need Help */}
        <div className="max-w-4xl mx-auto mt-16">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-heading font-medium mb-4">
                Still have questions?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                Can't find what you're looking for? Our community is here to help! 
                Post in the forums or reach out to us directly.
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                <Link to="/forums">
                  <Button variant="outline">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Ask in Forums
                  </Button>
                </Link>
                <Link to="/privacy">
                  <Button variant="outline">
                    <Shield className="w-4 h-4 mr-2" />
                    Privacy Policy
                  </Button>
                </Link>
                <a href="mailto:support@allamericandog.club">
                  <Button className="btn-primary">
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Support
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
